import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import ProductTierGrid from '../components/ProductTierGrid';
import EmbeddedPayment from '../components/EmbeddedPayment';
import { useStripe } from '../contexts/StripeContext';

// Product tier interface
interface ProductTier {
  id: string;
  name: string;
  description: string;
  price_id: string;
  amount: number;
  currency: string;
  interval: string;
  features: string[];
  metadata: Record<string, string>;
}

// Validation types
interface ValidationState {
  isValid: boolean;
  message: string;
}

interface FormValidation {
  firstName: ValidationState;
  lastName: ValidationState;
  email: ValidationState;
  phoneNumber: ValidationState;
  password: ValidationState;
  confirmPassword: ValidationState;
}

interface RegistrationData {
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  password: string;
  selected_plan: {
    plan_id: string;
    price_id: string;
    plan_name: string;
  };
}

interface ApiError {
  field: string;
  message: string;
}

interface PaymentSetup {
  client_secret: string;
  stripe_customer_id: string;
  requires_payment: boolean;
}

interface ApiResponse {
  status: string;
  message?: string;
  user_id?: string;
  email_verified?: boolean;
  phone_verified?: boolean;
  errors?: ApiError[];
  payment_setup?: PaymentSetup;
}

const RegistrationPage: React.FC = () => {
  const navigate = useNavigate();
  
  // Form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // Product tier state
  const [productTiers, setProductTiers] = useState<ProductTier[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<ProductTier | null>(null);
  const [tiersLoading, setTiersLoading] = useState(true);
  const [tiersError, setTiersError] = useState<string | null>(null);
  
  // Validation state
  const [validation, setValidation] = useState<FormValidation>({
    firstName: { isValid: true, message: '' },
    lastName: { isValid: true, message: '' },
    email: { isValid: true, message: '' },
    phoneNumber: { isValid: true, message: '' },
    password: { isValid: true, message: '' },
    confirmPassword: { isValid: true, message: '' },
  });
  
  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Payment state
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentSetup, setPaymentSetup] = useState<PaymentSetup | null>(null);
  const [registeredUserID, setRegisteredUserID] = useState<string | null>(null);
  const [paymentIntentExpired, setPaymentIntentExpired] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});
  const { stripePromise, isLoading: stripeLoading, error: stripeError } = useStripe();

  // Fetch product tiers on component mount
  useEffect(() => {
    const fetchProductTiers = async () => {
      try {
        setTiersLoading(true);
        setTiersError(null);
        
        const response = await fetch('/api/product-tiers', {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        let tiersFromAPI: ProductTier[] = [];
        
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.tiers) {
            tiersFromAPI = data.tiers;
          }
        }

        // If no tiers are available from API, use mock data for demonstration
        if (tiersFromAPI.length === 0) {
          const mockTiers: ProductTier[] = [
            {
              id: 'starter',
              name: 'Starter',
              description: 'Perfect for individuals and small businesses getting started with phone number procurement.',
              price_id: 'price_starter',
              amount: 999, // $9.99
              currency: 'usd',
              interval: 'month',
              features: [
                '10 number searches per month',
                'Basic area code filtering',
                'Email notifications',
                'Standard support'
              ],
              metadata: {}
            },
            {
              id: 'professional',
              name: 'Professional',
              description: 'Advanced features for growing businesses that need more power and flexibility.',
              price_id: 'price_professional',
              amount: 2999, // $29.99
              currency: 'usd',
              interval: 'month',
              features: [
                '100 number searches per month',
                'Advanced filtering & patterns',
                'Automated provisioning',
                'Real-time notifications',
                'Priority support',
                'Custom area codes'
              ],
              metadata: { popular: 'true' }
            },
            {
              id: 'enterprise',
              name: 'Enterprise',
              description: 'Unlimited power for large organizations with enterprise-grade requirements.',
              price_id: 'price_enterprise',
              amount: 9999, // $99.99
              currency: 'usd',
              interval: 'month',
              features: [
                'Unlimited number searches',
                'Bulk number provisioning',
                'API access',
                'Custom integrations',
                'Dedicated account manager',
                'SLA guarantee',
                'Advanced analytics'
              ],
              metadata: {}
            }
          ];
          setProductTiers(mockTiers);
        } else {
          setProductTiers(tiersFromAPI);
        }
      } catch (err) {
        console.error('Error fetching product tiers:', err);
        setTiersError('Failed to load subscription plans. You can still register and choose a plan later.');
        setProductTiers([]);
      } finally {
        setTiersLoading(false);
      }
    };

    fetchProductTiers();
  }, []);

  // Plan selection handler
  const handlePlanSelect = (tier: ProductTier) => {
    setSelectedPlan(tier);
  };

  // Validation functions
  const validateName = (name: string, fieldName: string): ValidationState => {
    const trimmedName = name.trim();
    
    if (!trimmedName) {
      return { isValid: false, message: `${fieldName} is required` };
    }
    
    if (trimmedName.length < 1) {
      return { isValid: false, message: `${fieldName} must be at least 1 character long` };
    }
    
    if (trimmedName.length > 50) {
      return { isValid: false, message: `${fieldName} must be 50 characters or less` };
    }
    
    const validNamePattern = /^[a-zA-Z\s\-']+$/;
    if (!validNamePattern.test(trimmedName)) {
      return { isValid: false, message: `${fieldName} can only contain letters, spaces, hyphens, and apostrophes` };
    }
    
    return { isValid: true, message: '' };
  };

  const validateEmail = (email: string): ValidationState => {
    const trimmedEmail = email.trim();
    
    if (!trimmedEmail) {
      return { isValid: false, message: 'Email is required' };
    }
    
    if (trimmedEmail.length > 254) {
      return { isValid: false, message: 'Email must be 254 characters or less' };
    }
    
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailPattern.test(trimmedEmail)) {
      return { isValid: false, message: 'Please enter a valid email address' };
    }
    
    return { isValid: true, message: '' };
  };

  const validatePhoneNumber = (phone: string): ValidationState => {
    const trimmedPhone = phone.trim();
    
    if (!trimmedPhone) {
      return { isValid: false, message: 'Phone number is required' };
    }
    
    // First check if it's already in E.164 format
    const e164Pattern = /^\+1\d{10}$/;
    if (e164Pattern.test(trimmedPhone)) {
      return { isValid: true, message: '' };
    }
    
    // Try to convert common US/Canada formats to E.164
    const convertedPhone = convertToE164(trimmedPhone);
    if (convertedPhone && e164Pattern.test(convertedPhone)) {
      return { isValid: true, message: '' };
    }
    
    return { 
      isValid: false, 
      message: 'Please enter a valid US/Canada phone number (e.g., (555) 123-4567 or +15551234567)' 
    };
  };

  const validatePassword = (password: string): ValidationState => {
    if (!password) {
      return { isValid: false, message: 'Password is required' };
    }
    
    if (password.length < 8) {
      return { isValid: false, message: 'Password must be at least 8 characters long' };
    }
    
    if (password.length > 72) {
      return { isValid: false, message: 'Password must be 72 characters or less' };
    }
    
    return { isValid: true, message: '' };
  };

  const validateConfirmPassword = (confirmPassword: string, originalPassword: string): ValidationState => {
    if (!confirmPassword) {
      return { isValid: false, message: 'Please confirm your password' };
    }
    
    if (confirmPassword !== originalPassword) {
      return { isValid: false, message: 'Passwords do not match' };
    }
    
    return { isValid: true, message: '' };
  };

  // Convert various phone formats to E.164
  const convertToE164 = (phone: string): string | null => {
    // Remove all non-digit characters
    const digits = phone.replace(/\D/g, '');
    
    // Handle different US/Canada formats
    if (digits.length === 10) {
      // Assume US/Canada if 10 digits
      return `+1${digits}`;
    } else if (digits.length === 11 && digits.startsWith('1')) {
      // Remove leading 1 and add +1
      return `+${digits}`;
    }
    
    return null;
  };

  // Real-time validation handlers
  const handleFirstNameChange = (value: string) => {
    setFirstName(value);
    const validation = validateName(value, 'First name');
    setValidation(prev => ({ ...prev, firstName: validation }));
  };

  const handleLastNameChange = (value: string) => {
    setLastName(value);
    const validation = validateName(value, 'Last name');
    setValidation(prev => ({ ...prev, lastName: validation }));
  };

  const handleEmailChange = (value: string) => {
    setEmail(value);
    const validation = validateEmail(value);
    setValidation(prev => ({ ...prev, email: validation }));
  };

  const handlePhoneNumberChange = (value: string) => {
    setPhoneNumber(value);
    const validation = validatePhoneNumber(value);
    setValidation(prev => ({ ...prev, phoneNumber: validation }));
  };

  const handlePasswordChange = (value: string) => {
    setPassword(value);
    const validation = validatePassword(value);
    setValidation(prev => ({ ...prev, password: validation }));
    
    // Also revalidate confirm password if it has been entered
    if (confirmPassword) {
      const confirmValidation = validateConfirmPassword(confirmPassword, value);
      setValidation(prev => ({ ...prev, confirmPassword: confirmValidation }));
    }
  };

  const handleConfirmPasswordChange = (value: string) => {
    setConfirmPassword(value);
    const validation = validateConfirmPassword(value, password);
    setValidation(prev => ({ ...prev, confirmPassword: validation }));
  };

  // Comprehensive validation function with detailed error reporting
  const validateForm = (): boolean => {
    const errors: {[key: string]: string} = {};
    
    // First name validation
    if (!firstName.trim()) {
      errors.firstName = 'First name is required';
    } else if (firstName.trim().length < 2) {
      errors.firstName = 'First name must be at least 2 characters';
    } else if (firstName.trim().length > 50) {
      errors.firstName = 'First name must be less than 50 characters';
    }
    
    // Last name validation
    if (!lastName.trim()) {
      errors.lastName = 'Last name is required';
    } else if (lastName.trim().length < 2) {
      errors.lastName = 'Last name must be at least 2 characters';
    } else if (lastName.trim().length > 50) {
      errors.lastName = 'Last name must be less than 50 characters';
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email.trim()) {
      errors.email = 'Email is required';
    } else if (!emailRegex.test(email.trim())) {
      errors.email = 'Please enter a valid email address';
    } else if (email.trim().length > 254) {
      errors.email = 'Email address is too long';
    }
    
    // Phone number validation
    if (!phoneNumber.trim()) {
      errors.phoneNumber = 'Phone number is required';
    } else {
      const cleanPhone = phoneNumber.replace(/\D/g, '');
      if (cleanPhone.length < 10) {
        errors.phoneNumber = 'Phone number must be at least 10 digits';
      } else if (cleanPhone.length > 15) {
        errors.phoneNumber = 'Phone number is too long';
      }
    }
    
    // Password validation
    if (!password.trim()) {
      errors.password = 'Password is required';
    } else if (password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    } else if (password.length > 128) {
      errors.password = 'Password must be less than 128 characters';
    } else if (!/(?=.*[a-zA-Z])(?=.*\d)/.test(password)) {
      errors.password = 'Password must contain at least one letter and one number';
    }
    
    // Confirm password validation
    if (!confirmPassword.trim()) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (password !== confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    
    // Plan selection validation removed for free tier default
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Check if form is valid
  const isFormValid = () => {
    return validation.firstName.isValid && 
           validation.lastName.isValid && 
           validation.email.isValid && 
           validation.phoneNumber.isValid &&
           validation.password.isValid &&
           validation.confirmPassword.isValid &&
           firstName.trim() &&
           lastName.trim() &&
           email.trim() &&
           phoneNumber.trim() &&
           password.trim() &&
           confirmPassword.trim(); // Plan selection not required
  };

  // Submit handler
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Comprehensive validation before submission
    if (!validateForm()) {
      setShowError(true);
      const errorMessages = Object.values(validationErrors);
      if (errorMessages.length > 0) {
        // Show the first error message, or combine them if multiple
        if (errorMessages.length === 1) {
          setErrorMessage(errorMessages[0]);
        } else {
          setErrorMessage(`Please fix the following: ${errorMessages.join('; ')}`);
        }
      } else {
        setErrorMessage('Please fix all validation errors before submitting');
      }
      return;
    }
    
    setIsLoading(true);
    setShowError(false);
    setShowSuccess(false);
    setValidationErrors({});
    setPaymentIntentExpired(false);
    
    try {
      // Convert phone to E.164 format for API
      const e164Phone = convertToE164(phoneNumber) || phoneNumber;
      
      // All users are placed on free tier
      const registrationData: RegistrationData = {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: email.toLowerCase().trim(),
        phone_number: e164Phone,
        password: password,
        selected_plan: {
          plan_id: 'free',
          price_id: '',
          plan_name: 'Free',
        },
      };

      const response = await fetch('/api/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(registrationData),
      });

      const data: ApiResponse = await response.json();

      if (response.ok && data.status === 'success') {
        // New flow: all users start on Free tier, no payment required
        setShowSuccess(true);
        setSuccessMessage('Registration successful! You have been placed on the Free tier. You will be redirected to the login form. Please log in with your new credentials.');
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else {
        // Handle validation errors from backend
        if (data.errors && data.errors.length > 0) {
          const newValidation = { ...validation };
          data.errors.forEach((error: ApiError) => {
            if (error.field === 'first_name') {
              newValidation.firstName = { isValid: false, message: error.message };
            } else if (error.field === 'last_name') {
              newValidation.lastName = { isValid: false, message: error.message };
            } else if (error.field === 'email') {
              newValidation.email = { isValid: false, message: error.message };
            } else if (error.field === 'phone_number') {
              newValidation.phoneNumber = { isValid: false, message: error.message };
            }
          });
          setValidation(newValidation);
        }
        
        setShowError(true);
        setErrorMessage(data.message || 'Registration failed. Please try again.');
      }
    } catch (error) {
      console.error('Registration error:', error);
      setShowError(true);
      setErrorMessage('Unable to connect to the server. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  // Payment success handler
  const handlePaymentSuccess = (subscriptionId: string) => {
    setShowPaymentForm(false);
    setShowSuccess(true);
    setShowError(false);
    setPaymentIntentExpired(false);
    setSuccessMessage(`🎉 Payment successful! Your ${selectedPlan?.name} subscription is now active. You will be redirected to the login form. Please log in with your new credentials.`);

    // Clear form
    setFirstName('');
    setLastName('');
    setEmail('');
    setPhoneNumber('');
    setPassword('');
    setConfirmPassword('');
    setSelectedPlan(null);
    setPaymentSetup(null);
    setRegisteredUserID(null);

    // Reset validation
    setValidation({
      firstName: { isValid: true, message: '' },
      lastName: { isValid: true, message: '' },
      email: { isValid: true, message: '' },
      phoneNumber: { isValid: true, message: '' },
      password: { isValid: true, message: '' },
      confirmPassword: { isValid: true, message: '' },
    });

    // Redirect to login page
    setTimeout(() => {
      navigate('/login');
    }, 3000);
  };

  // Payment error handler
  const handlePaymentError = (error: string) => {
    // Check if error indicates expired payment intent
    if (error.toLowerCase().includes('expired') || error.toLowerCase().includes('invalid')) {
      setPaymentIntentExpired(true);
      setErrorMessage('Payment session has expired. Please refresh your payment setup below.');
    } else {
      setShowError(true);
      setErrorMessage(`Payment failed: ${error}. Please try again or contact support.`);
    }
  };

  // Handle expired payment intent - create new payment setup
  const handleRefreshPaymentSetup = async () => {
    if (!registeredUserID || !selectedPlan) {
      setShowError(true);
      setErrorMessage('Unable to refresh payment setup. Please restart the registration process.');
      return;
    }

    setIsLoading(true);
    setPaymentIntentExpired(false);
    setShowError(false);

    try {
      const refreshData = {
        user_id: registeredUserID,
        selected_plan: {
          plan_id: selectedPlan.id,
          price_id: selectedPlan.price_id,
          plan_name: selectedPlan.name,
        },
      };

      const response = await fetch('/api/register/payment-setup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(refreshData),
      });

      const data = await response.json();

      if (response.ok && data.status === 'success' && data.payment_setup) {
        setPaymentSetup(data.payment_setup);
        setSuccessMessage('Payment setup refreshed. Please complete your payment below.');
      } else {
        setShowError(true);
        setErrorMessage(data.message || 'Failed to refresh payment setup. Please try again.');
      }
    } catch (error) {
      console.error('Payment setup refresh error:', error);
      setShowError(true);
      setErrorMessage('Unable to refresh payment setup. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container className="py-5">
      <Row className="justify-content-center">
        <Col xs={12} md={10} lg={8}>
          <div className="text-center mb-4">
            <h3 className="display-6 mb-2">✨ Create Account</h3>
            <p className="text-muted">Join the Pouncer community</p>
          </div>
          
          <Card className="shadow-sm">
            <Card.Body className="p-4">
              {showSuccess && (
                <Alert variant="success" className="mb-4">
                  <strong>🎉 Success!</strong> {successMessage}
                </Alert>
              )}
              
              {showError && (
                <Alert variant="danger" className="mb-4">
                  <strong>❌ Error:</strong> {errorMessage}
                </Alert>
              )}
              
              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>First Name</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter your first name"
                        value={firstName}
                        onChange={(e) => handleFirstNameChange(e.target.value)}
                        isValid={firstName.length > 0 && validation.firstName.isValid}
                        isInvalid={firstName.length > 0 && !validation.firstName.isValid}
                        required
                        data-testid="register-first-name"
                      />
                      {(!validation.firstName.isValid || validationErrors.firstName) && (
                        <Form.Control.Feedback type="invalid">
                          {validation.firstName.message || validationErrors.firstName}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>
                  
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Last Name</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter your last name"
                        value={lastName}
                        onChange={(e) => handleLastNameChange(e.target.value)}
                        isValid={lastName.length > 0 && validation.lastName.isValid}
                        isInvalid={lastName.length > 0 && !validation.lastName.isValid}
                        required
                        data-testid="register-last-name"
                      />
                      {(!validation.lastName.isValid || validationErrors.lastName) && (
                        <Form.Control.Feedback type="invalid">
                          {validation.lastName.message || validationErrors.lastName}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Email Address</Form.Label>
                  <Form.Control
                    type="email"
                    placeholder="Enter your email address"
                    value={email}
                    onChange={(e) => handleEmailChange(e.target.value)}
                    isValid={email.length > 0 && validation.email.isValid}
                    isInvalid={email.length > 0 && !validation.email.isValid}
                    required
                    data-testid="register-email"
                  />
                  {(!validation.email.isValid || validationErrors.email) && (
                    <Form.Control.Feedback type="invalid">
                      {validation.email.message || validationErrors.email}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label>Phone Number</Form.Label>
                  <Form.Control
                    type="tel"
                    placeholder="(555) 123-4567 or +15551234567"
                    value={phoneNumber}
                    onChange={(e) => handlePhoneNumberChange(e.target.value)}
                    isValid={phoneNumber.length > 0 && validation.phoneNumber.isValid}
                    isInvalid={phoneNumber.length > 0 && !validation.phoneNumber.isValid}
                    required
                    data-testid="register-phone"
                  />
                  {(!validation.phoneNumber.isValid || validationErrors.phoneNumber) && (
                    <Form.Control.Feedback type="invalid" data-testid="register-phone-error">
                      {validation.phoneNumber.message || validationErrors.phoneNumber}
                    </Form.Control.Feedback>
                  )}
                  <Form.Text className="text-muted">
                    US/Canada phone numbers only. We'll convert it to the standard format.
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Password</Form.Label>
                  <Form.Control
                    type="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => handlePasswordChange(e.target.value)}
                    isValid={password.length > 0 && validation.password.isValid}
                    isInvalid={password.length > 0 && !validation.password.isValid}
                    required
                    data-testid="register-password"
                  />
                  {(!validation.password.isValid || validationErrors.password) && (
                    <Form.Control.Feedback type="invalid">
                      {validation.password.message || validationErrors.password}
                    </Form.Control.Feedback>
                  )}
                  <Form.Text className="text-muted">
                    Must be at least 8 characters with one letter and one number.
                    {password.length > 0 && (
                      <span className={password.length >= 8 ? 'text-success' : 'text-warning'}>
                        {' '}({password.length}/8 characters)
                      </span>
                    )}
                  </Form.Text>
                </Form.Group>
                
                <Form.Group className="mb-4">
                  <Form.Label>Confirm Password</Form.Label>
                  <Form.Control
                    type="password"
                    placeholder="Confirm your password"
                    value={confirmPassword}
                    onChange={(e) => handleConfirmPasswordChange(e.target.value)}
                    isValid={confirmPassword.length > 0 && validation.confirmPassword.isValid}
                    isInvalid={confirmPassword.length > 0 && !validation.confirmPassword.isValid}
                    required
                    data-testid="register-confirm-password"
                  />
                  {(!validation.confirmPassword.isValid || validationErrors.confirmPassword) && (
                    <Form.Control.Feedback type="invalid" data-testid="register-confirm-password-error">
                      {validation.confirmPassword.message || validationErrors.confirmPassword}
                    </Form.Control.Feedback>
                  )}
                  {confirmPassword.length > 0 && validation.confirmPassword.isValid && (
                    <Form.Control.Feedback type="valid" data-testid="register-passwords-match">
                      Passwords match!
                    </Form.Control.Feedback>
                  )}
                  <Form.Text className="text-muted">
                    You will start on the <strong>Free Tier</strong> so you can explore how the system works. You’ll be able to upgrade to a paid plan at any time from your account page.
                  </Form.Text>
                </Form.Group>

                {/* Product Tier Selection Section */}
                {/* Product Tier Selection Section removed for free tier default */}
                {/*
                <div className="mb-4">
                  <div className="mb-3">
                    <h5 className="mb-2">Choose Your Plan <span className="text-danger">*</span></h5>
                    <p className="text-muted small mb-3">
                      Select a subscription plan to complete your registration. Plan selection is required.
                    </p>
                  </div>

                  {tiersLoading && (
                    <div className="text-center py-4">
                      <Spinner animation="border" size="sm" className="me-2" />
                      <span className="text-muted">Loading subscription plans...</span>
                    </div>
                  )}

                  {tiersError && (
                    <Alert variant="warning" className="mb-3">
                      <small>{tiersError}</small>
                    </Alert>
                  )}

                  {!tiersLoading && productTiers.length > 0 && (
                    <>
                      <ProductTierGrid
                        tiers={productTiers}
                        currentSubscription={null}
                        onSelectPlan={handlePlanSelect}
                        displayOnly={false}
                        data-testid="register-plan-grid"
                      />
                      
                      {selectedPlan && (
                        <Alert variant="success" className="mt-3 mb-0" data-testid="register-selected-plan">
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <strong>Selected Plan:</strong> {selectedPlan.name} - ${(selectedPlan.amount / 100).toFixed(2)}/{selectedPlan.interval}
                            </div>
                          </div>
                        </Alert>
                      )}
                    </>
                  )}

                      {!selectedPlan && (
                        <Alert variant="info" className="mt-3 mb-0" data-testid="register-no-plan-selected">
                          <small>
                            ⚠️ Please select a subscription plan above to continue with registration
                          </small>
                        </Alert>
                      )}

                      {validationErrors.selectedPlan && (
                        <Alert variant="danger" className="mt-3 mb-0">
                          <small>
                            <strong>Error:</strong> {validationErrors.selectedPlan}
                          </small>
                        </Alert>
                      )}
                    </>
                  )}
                </div>
                */}
                  <div className="d-grid mb-6">
                    <Button 
                      variant="primary" 
                      type="submit" 
                      size="lg" 
                      disabled={!isFormValid() || isLoading}
                      data-testid="register-submit"
                    >
                      {isLoading ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="me-2"
                          />
                          Creating Account...
                        </>
                      ) : (
                        'Create Account'
                      )}
                    </Button>
                  </div>
                </Form>
              
              {/* Payment form section - shown after successful registration */}
              {/*
              Payment form section - shown after successful registration
              {showPaymentForm && paymentSetup && registeredUserID && selectedPlan && (
                <>
                  <hr className="my-4" />
                  <div className="text-center mb-4">
                    <h5>💳 Complete Your Payment</h5>
                    <p className="text-muted">
                      Your account has been created! Please complete payment to activate your <strong>{selectedPlan.name}</strong> subscription.
                    </p>
                  </div>

                  Payment Intent Expired Warning
                  {paymentIntentExpired && (
                    <Alert variant="warning" className="mb-4">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <Alert.Heading className="h6 mb-1">⏰ Payment Session Expired</Alert.Heading>
                          <p className="mb-0">Your payment session has expired. Please refresh to create a new payment setup.</p>
                        </div>
                        <Button 
                          variant="outline-warning" 
                          size="sm"
                          onClick={handleRefreshPaymentSetup}
                          disabled={isLoading}
                        >
                          {isLoading ? (
                            <>
                              <Spinner size="sm" className="me-1" />
                              Refreshing...
                            </>
                          ) : (
                            'Refresh Payment'
                          )}
                        </Button>
                      </div>
                    </Alert>
                  )}
                  
                  Stripe Loading State
                  {stripeLoading && (
                    <Alert variant="info" className="mb-4">
                      <div className="d-flex align-items-center">
                        <Spinner size="sm" className="me-2" />
                        Loading payment system...
                      </div>
                    </Alert>
                  )}

                  Stripe Error State
                  {stripeError && (
                    <Alert variant="danger" className="mb-4">
                      <Alert.Heading className="h6 mb-1">Payment System Error</Alert.Heading>
                      <p className="mb-0">{stripeError}</p>
                    </Alert>
                  )}

                  Payment Form - only show when Stripe is loaded
                  {!stripeLoading && !stripeError && stripePromise && (
                    <EmbeddedPayment
                      stripePromise={stripePromise}
                      clientSecret={paymentSetup.client_secret}
                      userID={registeredUserID}
                      planInfo={{
                        plan_id: selectedPlan.id,
                        price_id: selectedPlan.price_id,
                        plan_name: selectedPlan.name,
                      }}
                      onSuccess={handlePaymentSuccess}
                      onError={handlePaymentError}
                    />
                  )}
                </>
              )}
              */}
              
              {/* Only show login link if not in payment flow */}
              {!showPaymentForm && (
                <>
                  <hr className="my-4" />
                  <div className="text-center">
                    <p className="text-muted mb-2">
                      Already have an account?
                    </p>
                    <Link 
                      to="/login"
                      className="btn btn-outline-primary btn-sm"
                    >
                      Sign In
                    </Link>
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
          
          <div className="text-center mt-4">
            <small className="text-muted">
              🔒 Your information is secure and will never be shared
            </small>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default RegistrationPage;
